import { Fragment, useEffect, useState } from 'react';
import type {
  PayoutSchedule,
  PayoutScheduleRow,
  PayoutScheduleRowType,
  PlatformKey,
  SavePayoutScheduleResponse,
} from '@clarity/contract';
import {
  usePayoutSchedule,
  usePayoutSchedules,
  useSavePayoutSchedule,
} from '../../queries/index.js';
import { useI18n } from '../../lib/i18n.js';
import { Modal } from '../Modal.js';

/**
 * Admin-only payout-schedule surface (clarity-payout-schedule Req 5), built in
 * the teal-glass world the agreed mock (`brand/payout-schedule-admin-mock.html`)
 * pins. The MAIN view is a LIST of the saved schedules (one row per platform +
 * month); an "Add schedule" button — and each row's "Edit" — opens a MODAL that
 * carries the entry form (platform + month pickers + the coverage-window rows).
 *
 * The admin transcribes Talabat's monthly payout calendar — rows of `coverage
 * window → scheduled payout date`, plus a monthly-closing row — for a platform +
 * month; it is saved ONCE and shared across every store/brand on that platform,
 * effective-dated (append, never rewrite — editing appends a new version).
 *
 * Admin-only: the route guard in `AppRoutes` is the real gate (a client never
 * reaches it); the server independently enforces admin-only on every endpoint
 * (Req 2.1). RTL-safe (logical properties), `--font-num` tabular dates, WCAG 2.1
 * AA (labelled inputs, `role="status"`/`role="alert"`, focus-trapped modal).
 */

/** A fresh, blank window row (the admin fills the dates). */
function blankRow(): PayoutScheduleRow {
  return { type: 'window', coverageStart: null, coverageEnd: null, payoutDate: '', label: '' };
}

/** The current calendar month as `YYYY-MM` (the sensible default for a new schedule). */
function currentMonthKey(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** A `YYYY-MM-DD` (date input) ↔ ISO instant bridge. Empty string ⇒ null. */
function toIso(day: string): string | null {
  if (!day) return null;
  const ms = Date.parse(`${day}T00:00:00.000Z`);
  return Number.isNaN(ms) ? null : new Date(ms).toISOString();
}
function toDay(iso: string | null): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? '' : new Date(ms).toISOString().slice(0, 10);
}

/** A human platform label for the list (the key is the stored value). */
const PLATFORM_LABEL: Record<string, string> = { talabat: 'Talabat', careem: 'Careem' };

/** The coverage span of a schedule, as `from → to` days, for the list subtext (`null` when none). */
function coverageSpan(rows: readonly PayoutScheduleRow[]): string | null {
  const days = rows
    .flatMap((r) => [r.coverageStart, r.coverageEnd])
    .filter((d): d is string => !!d)
    .map((d) => Date.parse(d))
    .filter(Number.isFinite);
  if (days.length === 0) return null;
  const min = toDay(new Date(Math.min(...days)).toISOString());
  const max = toDay(new Date(Math.max(...days)).toISOString());
  return `${min} → ${max}`;
}

/** `updatedAt` as a short local date for the list column. */
function shortDate(iso: string): string {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? new Date(ms).toISOString().slice(0, 10) : '—';
}

export function PayoutScheduleAdmin() {
  const { t } = useI18n();
  const schedules = usePayoutSchedules();
  // The schedule being edited (null = add new), or `false` = modal closed.
  const [editing, setEditing] = useState<PayoutSchedule | null | false>(false);
  // The schedule ids whose rows are expanded inline.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const list: PayoutSchedule[] = schedules.data?.status === 200 ? schedules.data.body : [];

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <section>
      <div className="page-head">
        <div>
          <h2>{t('payoutSchedule.title')}</h2>
          <p className="sub">{t('payoutSchedule.sub')}</p>
        </div>
        <button type="button" className="primary" onClick={() => setEditing(null)}>
          {t('payoutSchedule.add')}
        </button>
      </div>

      <div className="card">
        {schedules.isLoading ? (
          <p className="muted payout-list__state" role="status">
            {t('payoutSchedule.list.loading')}
          </p>
        ) : schedules.isError ? (
          <p className="error payout-list__state" role="alert">
            {t('payoutSchedule.list.error')}
          </p>
        ) : list.length === 0 ? (
          <div className="payout-list__empty">
            <h3>{t('payoutSchedule.list.empty.title')}</h3>
            <p className="muted">{t('payoutSchedule.list.empty.body')}</p>
          </div>
        ) : (
          <table className="data-table payout-list__table">
            <thead>
              <tr>
                <th scope="col">{t('payoutSchedule.list.platform')}</th>
                <th scope="col">{t('payoutSchedule.list.month')}</th>
                <th scope="col">{t('payoutSchedule.list.rows')}</th>
                <th scope="col">{t('payoutSchedule.list.updated')}</th>
                <th scope="col">
                  <span className="sr-only">{t('payoutSchedule.list.edit')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {list.map((s) => {
                const span = coverageSpan(s.rows);
                const isOpen = expanded.has(s.id);
                return (
                  <Fragment key={s.id}>
                    <tr className="payout-list__row" data-open={isOpen}>
                      <td>
                        <span className="payout-list__plat">
                          <span className="payout-list__dot" aria-hidden="true" />
                          {PLATFORM_LABEL[s.platformKey] ?? s.platformKey}
                        </span>
                      </td>
                      <td>
                        <span className="payout-list__month">{s.month}</span>
                        {span && <span className="payout-list__span">{span}</span>}
                      </td>
                      <td>{s.rows.length}</td>
                      <td>{shortDate(s.updatedAt)}</td>
                      <td>
                        <div className="payout-list__actions">
                          <button
                            type="button"
                            className="sm payout-list__toggle"
                            aria-expanded={isOpen}
                            onClick={() => toggle(s.id)}
                          >
                            <span className="payout-list__chevron" aria-hidden="true">
                              ▸
                            </span>
                            {isOpen
                              ? t('payoutSchedule.list.hidePayouts')
                              : t('payoutSchedule.list.showPayouts')}
                          </button>
                          <button type="button" className="sm" onClick={() => setEditing(s)}>
                            {t('payoutSchedule.list.edit')}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="payout-list__detail">
                        <td colSpan={5}>
                          <ScheduleDetail rows={s.rows} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editing !== false && (
        <PayoutScheduleModal schedule={editing} onClose={() => setEditing(false)} />
      )}
    </section>
  );
}

/**
 * The inline (read-only) detail for an expanded list row: the schedule's rows as
 * a compact table — each coverage window → payout date, with the type as a chip
 * (a `monthly_closing` row shows "—" for coverage and a distinct chip). Lets the
 * admin see what a month's schedule contains without opening the edit modal.
 */
function ScheduleDetail({ rows }: { rows: readonly PayoutScheduleRow[] }) {
  const { t } = useI18n();
  return (
    <div className="payout-detail">
      <table className="payout-detail__table">
        <thead>
          <tr>
            <th scope="col">{t('payoutSchedule.col.type')}</th>
            <th scope="col">{t('payoutSchedule.list.coverage')}</th>
            <th scope="col">{t('payoutSchedule.col.payout')}</th>
            <th scope="col">{t('payoutSchedule.col.label')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const closing = r.type === 'monthly_closing';
            return (
              <tr key={i}>
                <td>
                  <span className={`payout-chip${closing ? ' payout-chip--closing' : ''}`}>
                    {closing ? t('payoutSchedule.type.closing') : t('payoutSchedule.type.window')}
                  </span>
                </td>
                <td className="num">
                  {closing || !r.coverageStart || !r.coverageEnd ? (
                    <span className="muted">—</span>
                  ) : (
                    `${toDay(r.coverageStart)} → ${toDay(r.coverageEnd)}`
                  )}
                </td>
                <td className="num">{toDay(r.payoutDate)}</td>
                <td className="muted">{r.label || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The add/edit modal: platform + month pickers, the coverage-window rows table,
 * and Save/Cancel. For an EDIT (`schedule` given) the pickers seed from that
 * schedule and its rows load; for an ADD (`schedule === null`) the pickers
 * default to Talabat + the current month and the saved schedule (if any) for the
 * chosen platform/month loads so the admin corrects rather than re-enters.
 * Saving appends a new effective-dated version and closes on success.
 */
function PayoutScheduleModal({
  schedule,
  onClose,
}: {
  schedule: PayoutSchedule | null;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [platformKey, setPlatformKey] = useState<PlatformKey>(
    (schedule?.platformKey as PlatformKey) ?? 'talabat',
  );
  const [month, setMonth] = useState<string>(schedule?.month ?? currentMonthKey());
  const [rows, setRows] = useState<PayoutScheduleRow[]>(
    schedule && schedule.rows.length > 0 ? schedule.rows.map((r) => ({ ...r })) : [blankRow()],
  );

  // On ADD, load whatever is currently saved for the chosen platform + month so
  // a re-entry corrects it. On EDIT we already seeded from the passed schedule;
  // still keep in sync if the admin switches platform/month in the picker.
  const existing = usePayoutSchedule(platformKey, month);
  const loaded = existing.data?.status === 200 ? existing.data.body : null;
  useEffect(() => {
    if (existing.isLoading) return;
    setRows(loaded && loaded.rows.length > 0 ? loaded.rows.map((r) => ({ ...r })) : [blankRow()]);
  }, [loaded, existing.isLoading, platformKey, month]);

  const save = useSavePayoutSchedule();
  const saved: SavePayoutScheduleResponse | null =
    save.data?.status === 200 ? save.data.body : null;

  // Close once a save lands cleanly (no warnings to show); keep the modal open
  // when warnings came back so the admin sees which rows were skipped.
  useEffect(() => {
    if (saved && saved.warnings.length === 0) onClose();
  }, [saved, onClose]);

  function updateRow(i: number, patch: Partial<PayoutScheduleRow>) {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function setType(i: number, type: PayoutScheduleRowType) {
    updateRow(i, type === 'monthly_closing' ? { type, coverageStart: null, coverageEnd: null } : { type });
  }
  function addRow() {
    setRows((prev) => [...prev, blankRow()]);
  }
  function removeRow(i: number) {
    setRows((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));
  }
  function onSave() {
    save.mutate({ body: { platformKey, month, rows } });
  }

  const title = schedule ? t('payoutSchedule.modal.editTitle') : t('payoutSchedule.modal.addTitle');

  return (
    <Modal title={title} onClose={onClose} className="modal payout-admin">
      <div className="payout-admin__pickers">
        <label className="field">
          <span>{t('payoutSchedule.platform')}</span>
          <select value={platformKey} onChange={(e) => setPlatformKey(e.target.value as PlatformKey)}>
            <option value="talabat">Talabat</option>
            <option value="careem">Careem</option>
          </select>
        </label>
        <label className="field">
          <span>{t('payoutSchedule.month')}</span>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </label>
      </div>

      <div className="payout-rows">
        {rows.map((r, i) => {
          const closing = r.type === 'monthly_closing';
          return (
            <div className="payout-row" key={i} data-closing={closing}>
              <div className="payout-row__top">
                <span className="payout-row__num">{t('payoutSchedule.row', { n: i + 1 })}</span>
                <button
                  type="button"
                  className="ghost payout-row__remove"
                  onClick={() => removeRow(i)}
                  aria-label={t('payoutSchedule.removeRow')}
                  disabled={rows.length === 1}
                >
                  ✕
                </button>
              </div>
              <div className="payout-row__grid">
                <label className="payout-row__field payout-row__field--type">
                  <span>{t('payoutSchedule.col.type')}</span>
                  <select
                    value={r.type}
                    onChange={(e) => setType(i, e.target.value as PayoutScheduleRowType)}
                  >
                    <option value="window">{t('payoutSchedule.type.window')}</option>
                    <option value="monthly_closing">{t('payoutSchedule.type.closing')}</option>
                  </select>
                </label>
                <label className="payout-row__field payout-row__field--cov">
                  <span>{t('payoutSchedule.col.from')}</span>
                  <input
                    type="date"
                    value={toDay(r.coverageStart)}
                    disabled={closing}
                    onChange={(e) => updateRow(i, { coverageStart: toIso(e.target.value) })}
                  />
                </label>
                <label className="payout-row__field payout-row__field--cov">
                  <span>{t('payoutSchedule.col.to')}</span>
                  <input
                    type="date"
                    value={toDay(r.coverageEnd)}
                    disabled={closing}
                    onChange={(e) => updateRow(i, { coverageEnd: toIso(e.target.value) })}
                  />
                </label>
                <label className="payout-row__field">
                  <span>{t('payoutSchedule.col.payout')}</span>
                  <input
                    type="date"
                    value={toDay(r.payoutDate)}
                    onChange={(e) => updateRow(i, { payoutDate: toIso(e.target.value) ?? '' })}
                  />
                </label>
                <label className="payout-row__field">
                  <span>{t('payoutSchedule.col.label')}</span>
                  <input
                    type="text"
                    value={r.label ?? ''}
                    placeholder={t('payoutSchedule.labelPlaceholder')}
                    onChange={(e) => updateRow(i, { label: e.target.value })}
                  />
                </label>
              </div>
            </div>
          );
        })}
      </div>

      <button type="button" className="sm payout-admin__add-row" onClick={addRow}>
        {t('payoutSchedule.addRow')}
      </button>

      <p className="muted payout-admin__hint">{t('payoutSchedule.hint')}</p>

      {save.isError && (
        <p role="alert" className="error">
          {t('payoutSchedule.saveError')}
        </p>
      )}
      {saved && saved.warnings.length > 0 && (
        <div role="status" className="payout-admin__saved">
          <p>{t('payoutSchedule.saved', { count: saved.schedule.rows.length })}</p>
          <ul className="payout-admin__warnings">
            {saved.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="modal__actions">
        <button type="button" className="ghost" onClick={onClose}>
          {t('payoutSchedule.cancel')}
        </button>
        <button type="button" className="primary" onClick={onSave} disabled={save.isPending}>
          {save.isPending ? t('payoutSchedule.saving') : t('payoutSchedule.save')}
        </button>
      </div>
    </Modal>
  );
}
